import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { setAuthProvider, markAuthAsReady } from '@/api/auth-helper';

interface AuthContextValue {
  isLoaded: boolean;
  isSignedIn: boolean;
  isAuthReady: boolean;
  userId?: string | null;
  orgId?: string | null;
  getToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
}

/**
 * The auth state when there is one, `undefined` when the caller renders outside
 * an AuthProvider. For presentation-only concerns that merely want to scope
 * something to the signed-in user (a storage key, say) and must still render
 * without a provider. Anything that needs a token or an identity must use
 * `useAuthContext` and fail loudly instead.
 */
export function useOptionalAuthContext() {
  return useContext(AuthContext);
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const auth = useAuth();
  const { isLoaded, isSignedIn, userId, orgId } = auth;
  const [isAuthReady, setIsAuthReady] = useState(false);

  const getToken = useCallback(async (): Promise<string> => {
    if (!auth.getToken) {
      console.warn('Auth not loaded or user not signed in');
      return '';
    }

    try {
      const token = await auth.getToken({ template: 'backend_api' });
      return token || '';
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return '';
    }
  }, [auth]);

  // Memoized so consumers of useAuthContext only re-render when the auth state
  // actually changes, not on every provider render.
  const value: AuthContextValue = useMemo(
    () => ({
      isLoaded,
      isSignedIn: isSignedIn ?? false,
      isAuthReady,
      userId,
      orgId,
      getToken,
    }),
    [isLoaded, isSignedIn, isAuthReady, userId, orgId, getToken],
  );

  // Set up the auth provider for non-React code
  useEffect(() => {
    if (isLoaded) {
      if (isSignedIn) {
        setAuthProvider(getToken);
        markAuthAsReady();
        setIsAuthReady(true);
      } else {
        // If not signed in, still mark as ready to prevent infinite loading
        markAuthAsReady();
        setIsAuthReady(true);
      }
    }
  }, [isLoaded, isSignedIn, getToken]);

  // Show loading state while auth is initializing
  if (!isAuthReady && isLoaded && isSignedIn) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            Initializing authentication...
          </p>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}